import React, { useState, useEffect } from 'react';
import { View, Text, ScrollView, TouchableOpacity, TextInput, StyleSheet, Platform } from 'react-native';
import Constants from 'expo-constants';
import LoadingSpinner from './LoadingSpinner';

/**
 * Email Testing Tab for Admin Panel
 * Allows testing all email templates via SendGrid
 */
const EmailTestingTab = () => {
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(null);
  const [results, setResults] = useState({});
  const [emailCaptures, setEmailCaptures] = useState([]);
  const [loadingCaptures, setLoadingCaptures] = useState(true);
  const [blastConfig, setBlastConfig] = useState({
    subject: '🚀 Big News from Bet Optimized!',
    headline: "We're Live!",
    subheadline: 'Your smarter betting journey starts now',
    bodyText: "We're excited to announce that Bet Optimized is now available! Join thousands of smart bettors who are using our platform to find the best odds and maximize their returns.",
    ctaText: 'Get Started Free',
    ctaUrl: 'https://betoptimized.com'
  });
  
  const functionsUrl = (() => {
    if (typeof __DEV__ !== 'undefined' && __DEV__) {
      return 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1';
    }
    const base = (
      (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL) ||
      (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL) ||
      (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL) ||
      (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
    );
    return base || 'https://us-central1-bet-optimized-fe922.cloudfunctions.net';
  })();
  
  // Load email captures on mount
  useEffect(() => {
    loadEmailCaptures();
  }, []);
  
  const loadEmailCaptures = async () => {
    try {
      setLoadingCaptures(true);
      const res = await fetch(`${functionsUrl}/getEmailCaptures`);
      const data = await res.json();
      if (data.success) {
        setEmailCaptures(data.emails || []);
      }
    } catch (error) {
      console.error('[EmailTestingTab] Error loading captures:', error);
    } finally {
      setLoadingCaptures(false);
    }
  };
  
  const sendTestEmailRequest = async (type, options = {}) => {
    if (!testEmail.trim()) {
      alert('Please enter a test email address');
      return;
    }
    
    setSending(type);
    setResults(prev => ({ ...prev, [type]: { loading: true } }));
    
    try {
      const res = await fetch(`${functionsUrl}/sendTestEmail`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: testEmail.trim(),
          type,
          options
        })
      });
      
      const data = await res.json();
      
      if (data.success) {
        setResults(prev => ({ ...prev, [type]: { success: true, message: data.message, subject: data.subject } }));
      } else {
        setResults(prev => ({ ...prev, [type]: { error: data.error || 'Unknown error' } }));
      }
    } catch (error) {
      setResults(prev => ({ ...prev, [type]: { error: error.message } }));
    } finally {
      setSending(null);
    }
  };
  
  const sendBlastTest = async () => {
    if (!testEmail.trim()) {
      alert('Please enter a test email address');
      return;
    }
    
    setSending('blast-test');
    
    try {
      const res = await fetch(`${functionsUrl}/sendMarketingBlast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...blastConfig,
          testMode: true,
          testEmail: testEmail.trim()
        })
      });
      
      const data = await res.json();
      
      if (data.success) {
        setResults(prev => ({ 
          ...prev, 
          'blast-test': { 
            success: true, 
            message: `Test blast sent to ${data.sentTo}`,
            totalCaptures: data.totalEmailCaptures
          } 
        }));
      } else {
        setResults(prev => ({ ...prev, 'blast-test': { error: data.error } }));
      }
    } catch (error) {
      setResults(prev => ({ ...prev, 'blast-test': { error: error.message } }));
    } finally {
      setSending(null);
    }
  };
  
  const ResultBadge = ({ result }) => {
    if (!result) return null;
    if (result.loading) return <LoadingSpinner size={14} color="#8B5CF6" style={{ marginLeft: 8 }} />;
    if (result.success) return <Text style={styles.successText}>✓ Sent</Text>;
    if (result.error) return <Text style={styles.errorText}>✗ {result.error}</Text>;
    return null;
  };
  
  return (
    <ScrollView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.title}>📧 Email Testing</Text>
        <Text style={styles.subtitle}>Test SendGrid email templates</Text>
      </View>
      
      {/* Test Email Input */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Test Email Address</Text>
        <TextInput
          style={styles.input}
          value={testEmail}
          onChangeText={setTestEmail}
          placeholder="Enter email address..."
          placeholderTextColor="#64748B"
          keyboardType="email-address"
          autoCapitalize="none"
        />
        <Text style={styles.hint}>All test emails will be sent to this address</Text>
      </View>
      
      {/* Test Email Buttons */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Send Test Emails</Text>
        
        {/* Test Email */}
        <View style={styles.emailRow}>
          <View style={styles.emailInfo}>
            <Text style={styles.emailType}>✅ Basic Test</Text>
            <Text style={styles.emailDesc}>Verify SendGrid is configured correctly</Text>
          </View>
          <TouchableOpacity 
            style={[styles.sendBtn, sending === 'test' && styles.sendBtnDisabled]} 
            onPress={() => sendTestEmailRequest('test')}
            disabled={!!sending}
          >
            {sending === 'test' ? (
              <LoadingSpinner size={14} color="#fff" />
            ) : (
              <Text style={styles.sendBtnText}>Send</Text>
            )}
          </TouchableOpacity>
        </View>
        <ResultBadge result={results.test} />
        
        {/* Welcome Email */}
        <View style={styles.divider} />
        <View style={styles.emailRow}>
          <View style={styles.emailInfo}>
            <Text style={styles.emailType}>👋 Welcome Email</Text>
            <Text style={styles.emailDesc}>Sent to new users when they sign up</Text>
          </View>
          <TouchableOpacity 
            style={[styles.sendBtn, sending === 'welcome' && styles.sendBtnDisabled]} 
            onPress={() => sendTestEmailRequest('welcome', { userName: 'Test User' })}
            disabled={!!sending}
          >
            {sending === 'welcome' ? (
              <LoadingSpinner size={14} color="#fff" />
            ) : (
              <Text style={styles.sendBtnText}>Send</Text>
            )}
          </TouchableOpacity>
        </View>
        <ResultBadge result={results.welcome} />
        
        {/* Watch Notification Email */}
        <View style={styles.divider} />
        <View style={styles.emailRow}>
          <View style={styles.emailInfo}>
            <Text style={styles.emailType}>🔔 Watch Notification</Text>
            <Text style={styles.emailDesc}>Sent when watched fixture odds become available</Text>
          </View>
          <TouchableOpacity 
            style={[styles.sendBtn, styles.sendBtnGreen, sending === 'watch' && styles.sendBtnDisabled]} 
            onPress={() => sendTestEmailRequest('watch', {
              homeTeam: 'Los Angeles Lakers',
              awayTeam: 'Boston Celtics',
              sport: 'Basketball',
              league: 'NBA'
            })}
            disabled={!!sending}
          >
            {sending === 'watch' ? (
              <LoadingSpinner size={14} color="#fff" />
            ) : (
              <Text style={styles.sendBtnText}>Send</Text>
            )}
          </TouchableOpacity>
        </View>
        <ResultBadge result={results.watch} />
        
        {/* Marketing Blast Preview */}
        <View style={styles.divider} />
        <View style={styles.emailRow}>
          <View style={styles.emailInfo}>
            <Text style={styles.emailType}>🚀 Marketing Blast (Preview)</Text>
            <Text style={styles.emailDesc}>Preview of the marketing email blast</Text>
          </View>
          <TouchableOpacity 
            style={[styles.sendBtn, styles.sendBtnOrange, sending === 'blast' && styles.sendBtnDisabled]} 
            onPress={() => sendTestEmailRequest('blast', blastConfig)}
            disabled={!!sending}
          >
            {sending === 'blast' ? (
              <LoadingSpinner size={14} color="#fff" />
            ) : (
              <Text style={styles.sendBtnText}>Send</Text>
            )}
          </TouchableOpacity>
        </View>
        <ResultBadge result={results.blast} />
      </View>
      
      {/* Marketing Blast Configuration */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>🎯 Marketing Blast Configuration</Text>
        <Text style={styles.cardSubtitle}>Configure the email blast for landing page signups</Text>
        
        <View style={styles.inputGroup}>
          <Text style={styles.inputLabel}>Subject Line</Text>
          <TextInput
            style={styles.input}
            value={blastConfig.subject}
            onChangeText={(v) => setBlastConfig(prev => ({ ...prev, subject: v }))}
            placeholder="Email subject..."
            placeholderTextColor="#64748B"
          />
        </View>
        
        <View style={styles.inputGroup}>
          <Text style={styles.inputLabel}>Headline</Text>
          <TextInput
            style={styles.input}
            value={blastConfig.headline}
            onChangeText={(v) => setBlastConfig(prev => ({ ...prev, headline: v }))}
            placeholder="Main headline..."
            placeholderTextColor="#64748B"
          />
        </View>
        
        <View style={styles.inputGroup}>
          <Text style={styles.inputLabel}>Subheadline</Text>
          <TextInput
            style={styles.input}
            value={blastConfig.subheadline}
            onChangeText={(v) => setBlastConfig(prev => ({ ...prev, subheadline: v }))}
            placeholder="Supporting text..."
            placeholderTextColor="#64748B"
          />
        </View>
        
        <View style={styles.inputGroup}>
          <Text style={styles.inputLabel}>Body Text</Text>
          <TextInput
            style={[styles.input, styles.inputMultiline]}
            value={blastConfig.bodyText}
            onChangeText={(v) => setBlastConfig(prev => ({ ...prev, bodyText: v }))}
            placeholder="Email body content..."
            placeholderTextColor="#64748B"
            multiline
            numberOfLines={4}
          />
        </View>
        
        <View style={styles.row}>
          <View style={[styles.inputGroup, { flex: 1, marginRight: 8 }]}>
            <Text style={styles.inputLabel}>CTA Button Text</Text>
            <TextInput
              style={styles.input}
              value={blastConfig.ctaText}
              onChangeText={(v) => setBlastConfig(prev => ({ ...prev, ctaText: v }))}
              placeholder="Button text..."
              placeholderTextColor="#64748B"
            />
          </View>
          <View style={[styles.inputGroup, { flex: 1, marginLeft: 8 }]}>
            <Text style={styles.inputLabel}>CTA URL</Text>
            <TextInput
              style={styles.input}
              value={blastConfig.ctaUrl}
              onChangeText={(v) => setBlastConfig(prev => ({ ...prev, ctaUrl: v }))}
              placeholder="https://..."
              placeholderTextColor="#64748B"
            />
          </View>
        </View>
        
        {/* Test Blast Button */}
        <TouchableOpacity 
          style={[styles.sendBlastBtn, sending === 'blast-test' && styles.sendBtnDisabled]} 
          onPress={sendBlastTest}
          disabled={!!sending}
        >
          {sending === 'blast-test' ? (
            <LoadingSpinner size={14} color="#fff" />
          ) : (
            <Text style={styles.sendBlastBtnText}>📨 Send Test Blast</Text>
          )}
        </TouchableOpacity>
        <ResultBadge result={results['blast-test']} />
      </View>
      
      {/* Email Captures List */}
      <View style={styles.card}>
        <View style={styles.capturesHeader}>
          <View>
            <Text style={styles.cardTitle}>📋 Email Captures</Text>
            <Text style={styles.cardSubtitle}>
              {loadingCaptures ? 'Loading...' : `${emailCaptures.length} emails captured from landing page`}
            </Text>
          </View>
          <TouchableOpacity style={styles.refreshBtn} onPress={loadEmailCaptures}>
            <Text style={styles.refreshBtnText}>↻ Refresh</Text>
          </TouchableOpacity>
        </View>
        
        {loadingCaptures ? (
          <LoadingSpinner size={32} color="#8B5CF6" style={{ padding: 40 }} />
        ) : emailCaptures.length === 0 ? (
          <View style={styles.emptyState}>
            <Text style={styles.emptyText}>No email captures yet</Text>
            <Text style={styles.emptySubtext}>Emails captured from your landing page will appear here</Text>
          </View>
        ) : (
          <View style={styles.emailList}>
            {emailCaptures.slice(0, 20).map((capture, i) => (
              <View key={capture.id} style={[styles.captureRow, i > 0 && styles.captureRowBorder]}>
                <View style={styles.captureInfo}>
                  <Text style={styles.captureEmail}>{capture.email}</Text>
                  <Text style={styles.captureDate}>
                    {capture.createdAt ? new Date(capture.createdAt).toLocaleDateString() : 'Unknown date'}
                    {capture.blastCount > 0 && ` • ${capture.blastCount} blasts sent`}
                  </Text>
                </View>
                {capture.unsubscribed && (
                  <Text style={styles.unsubscribedBadge}>Unsubscribed</Text>
                )}
              </View>
            ))}
            {emailCaptures.length > 20 && (
              <Text style={styles.moreText}>+ {emailCaptures.length - 20} more emails</Text>
            )}
          </View>
        )}
      </View>
      
      {/* SendGrid Info */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>ℹ️ SendGrid Configuration</Text>
        <View style={styles.configRow}>
          <Text style={styles.configLabel}>From Email:</Text>
          <Text style={styles.configValue}>hello@betoptimized.com</Text>
        </View>
        <View style={styles.configRow}>
          <Text style={styles.configLabel}>From Name:</Text>
          <Text style={styles.configValue}>Bet Optimized</Text>
        </View>
        <View style={styles.configRow}>
          <Text style={styles.configLabel}>Functions URL:</Text>
          <Text style={[styles.configValue, { fontSize: 11 }]} numberOfLines={1}>{functionsUrl}</Text>
        </View>
        <Text style={styles.configNote}>
          Ensure SENDGRID_API_KEY is set in your Firebase functions environment variables.
        </Text>
      </View>
      
      <View style={{ height: 40 }} />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0F172A',
  },
  header: {
    padding: 20,
    paddingBottom: 12,
  },
  title: {
    color: '#FFFFFF',
    fontSize: 28,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  subtitle: {
    color: '#94A3B8',
    fontSize: 14,
  },
  card: {
    backgroundColor: '#1E293B',
    marginHorizontal: 16,
    marginBottom: 16,
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#334155',
  },
  cardTitle: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 4,
  },
  cardSubtitle: {
    color: '#64748B',
    fontSize: 13,
    marginBottom: 16,
  },
  input: {
    backgroundColor: '#0F172A',
    borderWidth: 1,
    borderColor: '#334155',
    borderRadius: 8,
    padding: 14,
    color: '#FFFFFF',
    fontSize: 15,
  },
  inputMultiline: {
    minHeight: 100,
    textAlignVertical: 'top',
  },
  hint: {
    color: '#64748B',
    fontSize: 12,
    marginTop: 8,
  },
  emailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  emailInfo: {
    flex: 1,
  },
  emailType: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 2,
  },
  emailDesc: {
    color: '#94A3B8',
    fontSize: 13,
  },
  sendBtn: {
    backgroundColor: '#8B5CF6',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
    marginLeft: 12,
  },
  sendBtnGreen: {
    backgroundColor: '#10B981',
  },
  sendBtnOrange: {
    backgroundColor: '#F59E0B',
  },
  sendBtnDisabled: {
    opacity: 0.6,
  },
  sendBtnText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  divider: {
    height: 1,
    backgroundColor: '#334155',
    marginVertical: 16,
  },
  successText: {
    color: '#10B981',
    fontSize: 13,
    marginTop: 8,
    fontWeight: '500',
  },
  errorText: {
    color: '#EF4444',
    fontSize: 13,
    marginTop: 8,
  },
  inputGroup: {
    marginBottom: 16,
  },
  inputLabel: {
    color: '#94A3B8',
    fontSize: 13,
    marginBottom: 6,
    fontWeight: '500',
  },
  row: {
    flexDirection: 'row',
  },
  sendBlastBtn: {
    backgroundColor: '#8B5CF6',
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 10,
    alignItems: 'center',
    marginTop: 8,
  },
  sendBlastBtnText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  capturesHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  refreshBtn: {
    backgroundColor: 'rgba(139, 92, 246, 0.2)',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#8B5CF6',
  },
  refreshBtnText: {
    color: '#8B5CF6',
    fontSize: 13,
    fontWeight: '500',
  },
  emptyState: {
    padding: 40,
    alignItems: 'center',
  },
  emptyText: {
    color: '#94A3B8',
    fontSize: 16,
    marginBottom: 4,
  },
  emptySubtext: {
    color: '#64748B',
    fontSize: 13,
    textAlign: 'center',
  },
  emailList: {
    marginTop: 8,
  },
  captureRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
  },
  captureRowBorder: {
    borderTopWidth: 1,
    borderTopColor: '#334155',
  },
  captureInfo: {
    flex: 1,
  },
  captureEmail: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '500',
  },
  captureDate: {
    color: '#64748B',
    fontSize: 12,
    marginTop: 2,
  },
  unsubscribedBadge: {
    backgroundColor: 'rgba(239, 68, 68, 0.2)',
    color: '#EF4444',
    fontSize: 11,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    fontWeight: '500',
  },
  moreText: {
    color: '#64748B',
    fontSize: 13,
    textAlign: 'center',
    marginTop: 12,
    fontStyle: 'italic',
  },
  configRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  configLabel: {
    color: '#94A3B8',
    fontSize: 14,
  },
  configValue: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '500',
  },
  configNote: {
    color: '#64748B',
    fontSize: 12,
    marginTop: 12,
    fontStyle: 'italic',
  },
});

export default EmailTestingTab;

